import React, { useState } from 'react';
import { Logo } from './ui';
import { getCandidate } from '../data/storage';
import { supabase } from '../lib/supabase';
import { useLanguage, LangToggleBtn } from '../context/LanguageContext';
import { hashPassword, validateEmail, validatePassword } from '../utils/auth';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink:0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const F = "'Plus Jakarta Sans',sans-serif";
const inp  = { width:'100%', background:'#f8f9fb', border:'1px solid #e8edf5', borderRadius:10, padding:'12px 14px', color:'#1a2233', fontSize:16, fontFamily:F, outline:'none', marginBottom:12, minHeight:50, transition:'border-color .15s', boxSizing:'border-box' };
const btn  = { width:'100%', border:'none', padding:'14px', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:F, display:'flex', alignItems:'center', justifyContent:'center', gap:8, minHeight:52, letterSpacing:'.01em', transition:'opacity .15s' };

const phones = [
  { raw:'00201008182425', wa:'201008182425' },
  { raw:'00971565727742', wa:'971565727742' },
];

export default function LoginScreen({ branding, autoTrial, onCandidateLogin, onTeacherLogin, onStartTrial, onPlacementTest, onAcademy, onTeacherPortal, onAcademyLogin, onAcademyTeacherLogin, onForgotPassword, onTerms, onPrivacy, onLanding }) {
  const { lang, t, isRTL, toggleLanguage } = useLanguage();

  const [showAccount,   setShowAccount]   = useState(false);
  const [showTeacher,   setShowTeacher]   = useState(false);

  // Trial form
  const [trialName,  setTrialName]  = useState('');
  const [trialEmail, setTrialEmail] = useState('');
  const [trialWA,    setTrialWA]    = useState('');

  // Account form (email + password)
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPwd,   setAccountPwd]   = useState('');
  const [loading,      setLoading]      = useState(false);

  // Teacher/admin form (email + password)
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherPwd,   setTeacherPwd]   = useState('');

  const [error,  setError]  = useState('');
  const [mode,   setMode]   = useState(autoTrial ? 'trial' : 'hero'); // hero | trial
  const [trialDeviceLimited, setTrialDeviceLimited] = useState(false);

  async function handleAccountLogin() {
    setError('');
    const email = accountEmail.trim().toLowerCase();
    if (!validateEmail(email)) { setError(t('invalidEmail')); return; }

    setLoading(true);
    try {
      // 1. Try Supabase academy student (email + password)
      if (accountPwd) {
        const hash = await hashPassword(accountPwd);
        const { data, error: err } = await supabase
          .from('students').select('*').eq('email', email).eq('password_hash', hash).single();
        if (!err && data) {
          if (!data.active) {
            setError('Your account has not been activated yet. Contact Mr. Ahmed Salem on WhatsApp.');
          } else {
            if (onAcademyLogin) onAcademyLogin(data);
          }
          setLoading(false);
          return;
        }
        // Check if email exists but password wrong
        const { data: exists } = await supabase
          .from('students').select('id').eq('email', email).maybeSingle();
        if (exists) {
          setError(t('loginFailed'));
          setLoading(false);
          return;
        }
      }

      // 2. Fall back to localStorage IELTS candidates (email-only, no password)
      const c = getCandidate(email);
      if (!c) {
        setError('Email not found. Start a free trial or contact Mr. Ahmed Salem.');
        setLoading(false);
        return;
      }
      if (!c.active) {
        setError('Account deactivated. Contact Mr. Ahmed Salem.');
        setLoading(false);
        return;
      }
      onCandidateLogin(c);
    } catch { setError(t('connectionError')); }
    setLoading(false);
  }

  async function handleTeacherLogin() {
    setError('');
    if (!teacherPwd) { setError('Please enter your password.'); return; }
    setLoading(true);
    try {
      const hash = await hashPassword(teacherPwd);

      // Check admin_config first — password-only, no email required
      const { data: adminData, error: adminErr } = await supabase
        .from('admin_config').select('password_hash').eq('id', 1).single();
      if (!adminErr && adminData && hash === adminData.password_hash) {
        if (onTeacherLogin) onTeacherLogin();
        setLoading(false);
        return;
      }

      // Not admin — look up teacher by email + password hash
      const email = teacherEmail.trim().toLowerCase();
      if (!email) { setError(t('invalidEmail')); setLoading(false); return; }
      const { data, error: err } = await supabase
        .from('teachers').select('*').eq('email', email).eq('password_hash', hash).single();
      if (err || !data) {
        const { data: exists } = await supabase
          .from('teachers').select('id').eq('email', email).maybeSingle();
        setError(exists ? t('loginFailed') : 'Teacher not found. Contact Mr. Ahmed Salem.');
        setLoading(false);
        return;
      }
      if (!data.active) { setError('Account not active. Contact Mr. Ahmed Salem.'); setLoading(false); return; }
      const { saveTeacherSession } = await import('./academy/academyUtils');
      saveTeacherSession(data);
      if (onAcademyTeacherLogin) onAcademyTeacherLogin(data);
    } catch { setError(t('connectionError')); }
    setLoading(false);
  }

  function handleTrial() {
    setError('');
    setTrialDeviceLimited(false);
    if (localStorage.getItem('ic_trial_used')) { setTrialDeviceLimited(true); return; }
    const e = trialEmail.trim().toLowerCase();
    if (!validateEmail(e)) { setError(t('invalidEmail')); return; }
    onStartTrial(trialName.trim() || 'New Student', e);
  }

  return (
    <div dir={isRTL?'rtl':'ltr'} style={{ minHeight:'100vh', background:'linear-gradient(160deg,#f8f9fb 0%,#eef2f9 100%)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px 16px' }}>
      {/* Language toggle */}
      <div style={{ position:'fixed', top:16, right:isRTL?'auto':16, left:isRTL?16:'auto', zIndex:100 }}>
        <LangToggleBtn style={{ background:'#fff', border:'1px solid #e8edf5', color:'#1a2a4a', boxShadow:'0 1px 6px rgba(26,42,74,0.1)' }}/>
      </div>

      <div className="fade-up" style={{ width:'100%', maxWidth:440 }}>

        {/* Back to website */}
        {onLanding && (
          <div style={{ marginBottom:16 }}>
            <button onClick={onLanding} style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", padding:0, letterSpacing:'.05em' }}>
              ← Back
            </button>
          </div>
        )}

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:48, objectFit:'contain', cursor: onLanding ? 'pointer' : 'default' }} onClick={()=>onLanding&&onLanding()}/>
          </div>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.2em', textTransform:'uppercase', color:'#9aa3b8' }}>
            English Learning &amp; IELTS Coaching
          </div>
        </div>

        <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:18, padding:'28px 24px', boxShadow:'0 4px 28px rgba(26,42,74,0.10)' }}>

          {/* ── HERO MODE ── */}
          {mode === 'hero' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:23, fontWeight:700, color:'#1a2a4a', marginBottom:6, textAlign:'center' }}>
                {t('welcome')}
              </div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:24, lineHeight:1.65, textAlign:'center' }}>
                Join thousands of students improving their English and IELTS scores.
              </div>

              {/* PATH 1 — Free trial */}
              <button onClick={() => setMode('trial')}
                style={{ ...btn, background:'linear-gradient(135deg,#b8860b,#d4a017)', color:'#fff', boxShadow:'0 4px 16px rgba(184,134,11,0.3)', marginBottom:8, border:'2px solid transparent' }}
                onMouseEnter={e=>{ e.currentTarget.style.boxShadow='0 6px 22px rgba(184,134,11,0.45)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.boxShadow='0 4px 16px rgba(184,134,11,0.3)'; e.currentTarget.style.transform='none'; }}>
                🚀 {t('startTrial')} — {t('noRegNeeded')}
              </button>
              <div style={{ fontSize:12, color:'#9aa3b8', textAlign:'center', marginBottom:20 }}>
                Try 1 Smart Writing + 1 Test Mode practice, completely free
              </div>

              {/* PATH 2 — I have an account */}
              <button onClick={() => { setShowAccount(s=>!s); setShowTeacher(false); setError(''); }}
                style={{ ...btn, background:showAccount?'#1a2a4a':'#f8f9fb', color:showAccount?'#fff':'#1a2233', border:`1.5px solid ${showAccount?'#1a2a4a':'#e8edf5'}`, fontSize:14, fontWeight:600, marginBottom:showAccount?0:10, boxShadow:'none', borderRadius:10 }}>
                {showAccount ? '▲' : '▼'} {t('iHaveAccount')}
              </button>

              {showAccount && (
                <div style={{ background:'#f8f9fb', border:'1px solid #e8edf5', borderTop:'none', borderRadius:'0 0 10px 10px', padding:'16px 16px 14px', marginBottom:10 }}>
                  <div style={{ fontSize:12, color:'#5a6a88', marginBottom:12, lineHeight:1.65, background:'#eef2f9', borderRadius:8, padding:'9px 12px' }}>
                    <strong>Academy students</strong> — enter your registered email and password.<br/>
                    <strong>IELTS Writing Tools only</strong> — enter your email; password is not required.
                  </div>

                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.13em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:5 }}>
                    {t('emailAddress')}
                  </div>
                  <input type="email" value={accountEmail} onChange={e=>setAccountEmail(e.target.value)}
                    placeholder="yourname@email.com"
                    style={{ ...inp, fontSize:15, marginBottom:10 }} autoFocus/>

                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.13em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:5 }}>
                    Password <span style={{ fontFamily:F, textTransform:'none', letterSpacing:0, color:'#b0b8cc', fontSize:10 }}>(Academy students only)</span>
                  </div>
                  <input type="password" value={accountPwd} onChange={e=>setAccountPwd(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&handleAccountLogin()}
                    placeholder="••••••••"
                    style={{ ...inp, fontSize:15, marginBottom:6 }}/>

                  <div style={{ textAlign:'right', marginBottom:12 }}>
                    <button onClick={() => onForgotPassword && onForgotPassword()}
                      style={{ background:'none', border:'none', color:'#1a5fa8', fontSize:12, cursor:'pointer', fontFamily:F, padding:0, textDecoration:'underline' }}>
                      {t('forgotPassword')}
                    </button>
                  </div>

                  {error && <div style={{ color:'#c0392b', fontSize:12.5, marginBottom:10, padding:'9px 12px', background:'#fff5f5', borderRadius:8, border:'1px solid rgba(192,57,43,0.2)' }}>{error}</div>}

                  <button onClick={handleAccountLogin} disabled={loading}
                    style={{ ...btn, background:'#1a2a4a', color:'#fff', opacity:loading?0.7:1, fontSize:14, boxShadow:'none', minHeight:46 }}>
                    {loading ? 'Checking…' : `${t('login')} →`}
                  </button>
                </div>
              )}

              {/* Placement test */}
              <button onClick={onPlacementTest}
                style={{ ...btn, background:'#f8f9fb', color:'#1a2233', border:'1.5px solid #e8edf5', fontSize:13, fontWeight:500, marginBottom:16, minHeight:46, boxShadow:'none' }}>
                📝 {t('placementTest')} — Discover your English level
              </button>

              {/* PATH 3 — Teacher/Admin */}
              <button onClick={() => onTeacherPortal && onTeacherPortal()}
                style={{ ...btn, background:'transparent', color:'#9aa3b8', border:'1px solid transparent', fontSize:12, fontWeight:400, minHeight:38, letterSpacing:'.06em', fontFamily:"'JetBrains Mono',monospace", padding:'8px' }}>
                {t('teacherPortal')} →
              </button>
            </>
          )}

          {/* ── TRIAL MODE ── */}
          {mode === 'trial' && (
            <>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>
                Try Our AI Writing Tools Free
              </div>
              <div style={{ fontSize:13, color:'#5a6a88', marginBottom:16, lineHeight:1.6 }}>No payment. No commitment. Instant access.</div>

              <div style={{ background:'#f0faf4', border:'1px solid #c8ead8', borderRadius:10, padding:'12px 14px', marginBottom:18 }}>
                {['🤖 AI-powered essay feedback — see your band score instantly','✍️ Sentence-by-sentence guided writing practice','⏱ Exam simulation mode — timed, just like the real IELTS'].map((item,i)=>(
                  <div key={i} style={{ fontSize:13, color:'#1a5a3a', lineHeight:1.8 }}>{item}</div>
                ))}
              </div>

              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:6 }}>
                {t('fullName')} (optional)
              </div>
              <input value={trialName} onChange={e=>setTrialName(e.target.value)} placeholder="e.g. Ahmed Hassan" style={inp} autoFocus/>

              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:6 }}>
                {t('emailAddress')} *
              </div>
              <input type="email" value={trialEmail} onChange={e=>setTrialEmail(e.target.value)}
                placeholder="yourname@email.com" style={inp}/>

              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.14em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:6 }}>
                WhatsApp Number <span style={{ fontFamily:F, textTransform:'none', letterSpacing:0, color:'#b0b8cc', fontSize:10 }}>(optional — for follow-up)</span>
              </div>
              <input type="tel" value={trialWA} onChange={e=>setTrialWA(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&handleTrial()} placeholder="e.g. 201012345678" style={inp}/>

              {trialDeviceLimited && (
                <div style={{ color:'#7a4a00', fontSize:12.5, marginBottom:12, padding:'10px 14px', background:'#fff8e8', borderRadius:8, border:'1px solid #f0d890', lineHeight:1.6 }}>
                  You've already used your free trial on this device.{' '}
                  <a href="https://wa.me/201008182425" target="_blank" rel="noreferrer" style={{ color:'#b8860b', fontWeight:600, textDecoration:'underline' }}>
                    Contact us on WhatsApp to subscribe →
                  </a>
                </div>
              )}
              {error && <div style={{ color:'#c0392b', fontSize:12.5, marginBottom:12, padding:'9px 12px', background:'#fff5f5', borderRadius:8, border:'1px solid rgba(192,57,43,0.2)' }}>{error}</div>}

              <button onClick={handleTrial}
                style={{ ...btn, background:'linear-gradient(135deg,#1a2a4a,#2a3a5a)', color:'#fff', boxShadow:'0 3px 12px rgba(26,42,74,0.25)', marginBottom:12 }}>
                {t('getStarted')} — It's Free →
              </button>
              <div style={{ textAlign:'center' }}>
                <button onClick={()=>{ setMode('hero'); setError(''); setTrialDeviceLimited(false); }}
                  style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace" }}>
                  {isRTL ? 'رجوع →' : '← Back'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Contact numbers */}
        <div style={{ textAlign:'center', marginTop:20, display:'flex', flexDirection:'column', gap:5 }}>
          {phones.map(p=>(
            <a key={p.wa} href={`https://wa.me/${p.wa}`} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6, textDecoration:'none', color:'#25d366', fontSize:13, fontFamily:"'JetBrains Mono',monospace" }}>
              {WA_ICON}
              {p.raw}
            </a>
          ))}
        </div>

        {/* Footer links */}
        <div style={{ textAlign:'center', marginTop:14, display:'flex', justifyContent:'center', gap:16 }}>
          {[['Terms', onTerms], ['Privacy', onPrivacy]].map(([label, handler]) => (
            <button key={label} onClick={handler}
              style={{ background:'none', border:'none', color:'#b0b8cc', fontSize:11, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.07em', textDecoration:'underline' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
